import gsap from 'gsap'
import Swiper from 'swiper'
import { getBP } from '@utils/utils'

import BaseComponent from './_BaseComponent'
import Wall from './Wall'




/* BASE SLIDER
----------------------------------------------------------------------------------------------------*/

export default class FeaturedQuestions extends BaseComponent {

    constructor($el, namespace) {
        super('FeaturedQuestions', $el, namespace)

        this.on()
    }

    bindProperties() {
        if (!('slide' in this.$refs)) return

        this.canInteract = true
        this.slides = null
        this.interactionTimer = null
        this.BP = getBP()

        this.initSlider()
    }

    bindEvents() {
        if (!('slide' in this.$refs)) return

        const { $btns } = this.$refs

        this.addListeners(
            [$btns, 'click', e => this.onClick(e)],
            [document, 'layoutChange', () => this.onResize()],
        )
    }


    initSlider() {
        if (!('slide' in this.$refs) || APP.Layout.isMobile) return

        if (this.slider) {
            this.slider.destroy()
            this.slider = null
        }


        this.slider = new Swiper(this.$refs.slider[0], {
            // General
            slidesPerView: 'auto',
            grabCursor: true,
            speed: 400,
            virtualTranslate: true,
            simulateTouch: false,

            // Namespace
            containerModifierClass: 'slider--',
            wrapperClass: 'js-FeaturedQuestions-wrapper',
            slideClass: 'js-FeaturedQuestions-slide',
            slideActiveClass: 'is-active',
            slideVisibleClass: 'is-visible',
            slideNextClass: 'is-next',
            slidePrevClass: 'is-prev',
            slideDuplicateClass: 'is-dup',
            slideDuplicateActiveClass: 'is-dup--active',
            slideDuplicateNextClass: 'is-dup--next',
            slideDuplicatePrevClass: 'is-dup--prev',


            // Navigation
            keyboard: true,


            // Breakpoints
            breakpoints: {
                960: {
                    speed: 700,
                },
            },


            // Callbacks
            on: {
                init: s => this.onInit(s),
                progress: (s, p) => this.onProgress(p),
                slideChange: s => this.onSlideChange(s),
            },
        })

        this.setSize()

        // For first resize
        this.slides.forEach(s => s.wall.masonry?.layout())
    }



    /* Handlers
    --------------------------------------------------------- */

    onResize() {
        const actualBP = getBP()
        const breakpointHasChanged = this.BP !== actualBP

        this.BP = actualBP

        if (breakpointHasChanged) {
            if (APP.Layout.isMobile) {
                this.slider.destroy()
                this.slider = null
            } else {
                this.initSlider()
            }
        }


        this.setSize(breakpointHasChanged)
    }


    onInit(s) {
        this.slides = Array.from(s.slides).map(($el, i) => ({
            isCurrent: i === 0,
            slide: $el,
            $wall: this.$refs.wall.get(i),
            wall: new Wall(this.$refs.wall.eq(i), this.namespace),
        }))
    }


    onClick(e) {
        if (!this.slider || !this.canInteract) return
        this.shouldTranslateSmoothly = true


        const isNext = e.currentTarget.className.includes('next')

        clearTimeout(this.interactionTimer)

        if (isNext) {
            this.slider.slideNext()
        } else {
            this.slider.slidePrev()
        }
    }


    onSlideChange(s) {
        if (!this.slider || !this.canInteract) return

        clearTimeout(this.interactionTimer)
        this.canInteract = false

        const isNext = s.previousIndex < s.activeIndex

        this.$refs.slider[0].classList.remove('is-backwarding', 'is-forwarding')
        this.$refs.slider.addClass('is-translating')

        if (isNext) {
            this.$refs.slider.addClass('is-forwarding')
        } else {
            this.$refs.slider.addClass('is-backwarding')
        }

        this.setSize()
        gsap.set(this.$el, {
            '--progress': s.progress,
        })

        this.interactionTimer = setTimeout(() => {
            this.canInteract = true

            this.$refs.slider.removeClass('is-translating')
            this.shouldTranslateSmoothly = false
        }, 1000)
    }


    onProgress(p) {
        if (!('btns' in this.$refs)) return

        const prevIsDisabled = p <= 0
        const nextIsDisabled = p >= 1

        this.shouldTranslateSmoothly = true

        this.$refs.btns[0].toggleAttribute('disabled', prevIsDisabled)
        this.$refs.btns[1].toggleAttribute('disabled', nextIsDisabled)
    }



    /* Values
    --------------------------------------------------------- */

    setSize(reset) {
        if (!this.slider) return

        if (reset) {
            this.$refs.wrapper.css('height', null)
        }

        const activeSlide = this.slides[this.slider.activeIndex]
        const slideHeight = reset ? activeSlide.slide.scrollHeight : activeSlide.$wall.clientHeight

        this.$refs.wrapper.css('height', `${slideHeight}px`)
    }

}



